import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// Contexts and Hooks
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';

// API Service — adaptador REST contra lealify-base (reemplaza Firestore + Elasticsearch)
import { billingApi, periodFromCode, periodSuffixOf, periodAdjOf, MONTHS_BY_PERIOD, type AdminSubscriptionRow, type BillingPeriod } from '../../../api/billingApi';
import type { AdminUserFilters } from '../../../api/adminUsersApi';
import { UsoCelda, useUsoNegocios } from '../components/UsoNegocio';

// Types & Helpers
import type { PlanType } from '../../../types/user';
import { getPlanLabel, getPlanBadgeClasses } from '../../../utils/planPermissions';
import { VerticalBadge, VERTICAL_META, VERTICAL_FILTER_OPTIONS } from '../../../components/admin/VerticalBadge';
import { branchTotalPrice, branchBreakdown } from '../../../utils/branchPricing';
// Vencimientos: misma cuenta que el aviso del panel de admin (utils/subscriptionDates).
import { getDaysRemaining, isExpired, isExpiringSoon } from '../../../utils/subscriptionDates';

// UI Components & Icons
import Layout from '../../../components/layout/Layout';
import PaginationControls from '../../../components/common/PaginationControls';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import BrandLoader from '../../../components/common/BrandLoader';
import {
  Crown, Search, Check, Building, CreditCard, User, Users, Calendar,
  Loader2, Filter, X, ChevronDown, ChevronUp, Calendar as CalendarIcon
} from 'lucide-react';

//-----------------------------------------------------------------------------
// Interfaces
//-----------------------------------------------------------------------------
interface SubscriptionData {
  plan: PlanType;
  startDate: Date;
  endDate: Date | null;
  status: 'active' | 'expired' | 'cancelled';
  paymentMethod?: string | null;
}

interface UserSubscription {
  uid: string;
  email: string;
  displayName: string;
  businessName: string | null;
  businessId: string | null;
  vertical: string;
  subscription: SubscriptionData;
  createdAt: Date | null;
  phoneNumber?: string | null;
  countryCode?: string | null;
  maxBranches?: number;
  /** Precio ACORDADO con el cliente. null = tarifa estándar por sucursales. */
  customPrice?: number | null;
  /** Código del plan real ('pro_yearly'|'pro_monthly'|'free') → periodicidad del cobro. */
  planCode?: string;
}

interface MonthlyUsers {
  monthKey: string;
  displayMonth: string;
  users: UserSubscription[];
  count: number;
}

//-----------------------------------------------------------------------------
// Constants & Helpers
//-----------------------------------------------------------------------------
const PAYMENT_METHODS = [
  { id: 'manual', name: 'Pago Manual' },
  { id: 'transfer', name: 'Transferencia' },
  { id: 'other', name: 'Otro' }
];

const PAID_PLANS: PlanType[] = ['pro', 'premium'];
const isPaidPlan = (plan: PlanType): boolean => PAID_PLANS.includes(plan);

// Precio de sucursales = escalonado por volumen (ver utils/branchPricing.ts). Plan único.

const BRANCH_PRESETS = [1, 2, 3, 4, 5, 6, 7, 8, 10, 15, 20];

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;
const MAX_BUSINESS_NAME_LENGTH = 25;

// YYYY-MM-DD en hora LOCAL para <input type="date">. toISOString() convierte a UTC y un
// vencimiento a las 23:59:59 (Tijuana) caía al día siguiente: cada abrir+Guardar sumaba un día.
const toLocalYmd = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseDateSafe = (date: any): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (date.toDate && typeof date.toDate === 'function') return date.toDate();
  const parsed = new Date(date);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const formatLocalDateKey = (date: Date | null): string => {
  if (!date) return 'unknown';
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
};

const formatMonthDisplay = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: 'long' }).format(date).replace(/^\w/, c => c.toUpperCase());
};

const truncateName = (name: string | null, maxLength: number = MAX_BUSINESS_NAME_LENGTH): string => {
  if (!name) return 'N/A';
  return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
};

// Mapea una fila del adaptador REST (billingApi) → UserSubscription. Las fechas
// ya vienen como Date. `isTrial` se cuelga dentro de subscription (la UI lo lee
// como (subscription as any).isTrial).
const mapRow = (r: AdminSubscriptionRow): UserSubscription => ({
  uid: r.uid,
  email: r.email || '',
  displayName: r.displayName || 'Sin nombre',
  businessName: r.businessName,
  businessId: r.businessId,
  vertical: r.vertical || 'lealtad',
  createdAt: r.createdAt,
  maxBranches: r.maxBranches ?? 1,
  customPrice: r.customPrice ?? null,
  planCode: r.planCode || 'free',
  subscription: {
    plan: r.subscription.plan,
    startDate: r.subscription.startDate || new Date(),
    endDate: r.subscription.endDate,
    status: r.subscription.status as SubscriptionData['status'],
    paymentMethod: r.subscription.paymentMethod,
    isTrial: r.isTrial,
  } as any,
  isDemo: r.isDemo,
  isArchived: r.isArchived,
  avatarUrl: r.avatarUrl ?? null,
} as UserSubscription & { isDemo: boolean; isArchived: boolean; avatarUrl: string | null });


//-----------------------------------------------------------------------------
// Component
//-----------------------------------------------------------------------------
const AdminSubscriptionsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const { addError, clearErrors } = useError();

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const contentTopRef = useRef<HTMLDivElement>(null);
  const searchTriggedRef = useRef(false);
  const filterAppliedRef = useRef(false);

  const [users, setUsers] = useState<UserSubscription[]>([]);
  // «Cómo lo usan» + riesgo de renovación (admin F3): una llamada con los negocios visibles (uid = tenant_id).
  const idsUso = useMemo(() => users.map((u) => u.uid).filter(Boolean), [users]);
  const { uso, cargando: usoCargando } = useUsoNegocios(idsUso);
  // Demos/QA de la casa: ocultos por defecto (Carlos 8-oct-2026). El interruptor los muestra con etiqueta «Demo».
  const [showDemos, setShowDemos] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingPage, setLoadingPage] = useState<boolean>(false);
  const [pageChanging, setPageChanging] = useState<boolean>(false);
  const [loadingUpdate, setLoadingUpdate] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('free');
  const [endDate, setEndDate] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('manual');
  // Marca el ascenso como prueba/cortesía: mismas capacidades Pro/Premium pero NO cuenta como ingreso.
  const [isTrial, setIsTrial] = useState<boolean>(false);
  const [maxBranches, setMaxBranches] = useState<number>(1);
  // Periodicidad del cobro (13-ago-2026): 'yearly' = pro_yearly · 'monthly' = pro_monthly ·
  // 'semiannual' = pro_semiannual (25-ago-2026, cobro "6 y 6" de Marrieto).
  // Cambia el periodo que se activa al renovar (365 / 182 / 30 días) y la unidad del precio acordado.
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('yearly');
  // Precio ACORDADO con el cliente: texto vacío = usar la tarifa estándar por sucursales.
  const [customPriceText, setCustomPriceText] = useState<string>('');
  const [showCustomBranches, setShowCustomBranches] = useState(false);
  const [customBranchesText, setCustomBranchesText] = useState('');
  
  const [currentPageNum, setCurrentPageNum] = useState(1);
  const [hasMorePages, setHasMorePages] = useState<boolean>(false);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [filters, setFilters] = useState<AdminUserFilters>(() => {
    const planParam = searchParams.get('plan');
    const expirationStatusParam = searchParams.get('expirationStatus');
    const verticalParam = searchParams.get('vertical');
    return {
      plan: (planParam as AdminUserFilters['plan']) || 'all',
      vertical: (verticalParam as AdminUserFilters['vertical']) || 'all',
      dateFrom: null, dateTo: null,
      expirationStatus: (expirationStatusParam as 'all' | 'expired' | 'expiring-soon' | 'active') || 'all',
      onlyTrial: searchParams.get('trial') === '1'
    };
  });

  const [activeFilters, setActiveFilters] = useState<AdminUserFilters>(filters);

  // Pestaña inicial: 'paid' (vista de cobranza) salvo deep-link con filtros/búsqueda
  const [viewTab, setViewTab] = useState<'paid' | 'all'>(() =>
    (searchParams.get('plan') || searchParams.get('expirationStatus') || searchParams.get('term')) ? 'all' : 'paid'
  );

  const isPaidSel = isPaidPlan(selectedPlan);
  const isMonthlySel = billingPeriod === 'monthly';
  const mesesSel = MONTHS_BY_PERIOD[billingPeriod];
  // Tarifa estándar DEL PERIODO seleccionado: la escalonada es anual, así que se prorratea por
  // los meses del periodo (espejo de backend lib/pricing.js standardAmount — mantener
  // sincronizados). Antes solo contemplaba mensual (/12); con `pro_semiannual` (25-ago-2026)
  // el divisor pasa a salir de los meses, no de un booleano.
  const totalBranchPrice = isPaidSel
    ? Math.round(branchTotalPrice(maxBranches) * (mesesSel / 12))
    : 0;
  const periodSuffix = periodSuffixOf(mesesSel);

  useEffect(() => { if (clearErrors) clearErrors(); }, [clearErrors]);

  useEffect(() => {
    if (filterAppliedRef.current) return;
    const planParam = searchParams.get('plan');
    const expirationStatusParam = searchParams.get('expirationStatus');
    const termParam = searchParams.get('term');
    const trialParam = searchParams.get('trial') === '1';
    if (planParam || expirationStatusParam || termParam || trialParam) {
      setFilters({ plan: (planParam as AdminUserFilters['plan']) || 'all', dateFrom: null, dateTo: null, expirationStatus: (expirationStatusParam as 'all' | 'expired' | 'expiring-soon' | 'active') || 'all', onlyTrial: trialParam });
      // Deep-link desde Usuarios y Clientes: precarga la búsqueda con el email
      if (termParam) { setSearchTerm(termParam); setActiveSearchTerm(termParam); }
      filterAppliedRef.current = true;
    }
  }, [searchParams]);

  const fetchUsers = useCallback(async (pageToFetch: number, search: string, currentFilters: AdminUserFilters) => {
    if (!currentUser || currentUser.role !== 'admin') {
      setUsers([]); setLoading(false); setLoadingPage(false); setPageChanging(false); setTotalUsers(0); setHasMorePages(false); return;
    }
    setSuccess(null);
    if (pageToFetch === 1) setLoading(true);

    try {
      // ÚNICA fuente: lista completa de suscripciones (~cientos) → todo el
      // filtrado por pestaña/término/expiración/plan/fecha se hace EN CLIENTE.
      const rows = await billingApi.adminListSubscriptions();
      let mapped = rows.map(mapRow);
      // 18-oct-2026: los archivados (QA de altas, bajas) también se ocultan por defecto con el mismo interruptor.
      if (!showDemos) mapped = mapped.filter(u => !(u as any).isDemo && !(u as any).isArchived);
      if (search) {
        const lowerSearch = search.toLowerCase();
        mapped = mapped.filter(u => u.email.toLowerCase().includes(lowerSearch) || u.displayName.toLowerCase().includes(lowerSearch) || (u.businessName && u.businessName.toLowerCase().includes(lowerSearch)));
      }
      // Filtro de vertical: aplica a TODAS las vistas (cobranza / vencimiento / todos).
      if (currentFilters.vertical && currentFilters.vertical !== 'all') {
        mapped = mapped.filter(u => (u.vertical || 'lealtad') === currentFilters.vertical);
      }

      if (viewTab === 'paid' && (!currentFilters.expirationStatus || currentFilters.expirationStatus === 'all')) {
        // Vista de cobranza: suscripciones de paga ordenadas por vencimiento asc, sin paginar.
        const paidUsers = mapped.filter(u => isPaidPlan(u.subscription.plan));
        paidUsers.sort((a, b) => (a.subscription.endDate?.getTime() ?? Infinity) - (b.subscription.endDate?.getTime() ?? Infinity));
        setUsers(paidUsers); setTotalUsers(paidUsers.length); setHasMorePages(false);
      } else if (currentFilters.expirationStatus && currentFilters.expirationStatus !== 'all') {
        let paidUsers = mapped.filter(u => isPaidPlan(u.subscription.plan));
        if (currentFilters.expirationStatus === 'expired') {
          paidUsers = paidUsers.filter(u => isExpired(u.subscription.endDate));
        } else if (currentFilters.expirationStatus === 'expiring-soon') {
          paidUsers = paidUsers.filter(u => isExpiringSoon(u.subscription.endDate));
        } else {
          // 'active' = no vencidos
          paidUsers = paidUsers.filter(u => !isExpired(u.subscription.endDate));
        }
        // `?trial=1`: solo periodos de prueba (deep-link de la tarjeta «pruebas vencen esta semana»).
        if (currentFilters.onlyTrial) {
          paidUsers = paidUsers.filter(u => (u.subscription as any).isTrial === true);
        }
        const total = paidUsers.length;
        const startIndex = (pageToFetch - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        setUsers(paidUsers.slice(startIndex, endIndex)); setTotalUsers(total); setHasMorePages(endIndex < total);
      } else {
        // Pestaña "Todos los usuarios": filtros plan + rango de registro, paginado en cliente.
        let list = mapped;
        if (currentFilters.plan && currentFilters.plan !== 'all') {
          list = list.filter(u => u.subscription.plan === currentFilters.plan);
        }
        if (currentFilters.dateFrom) {
          const from = parseDateSafe(currentFilters.dateFrom);
          if (from) list = list.filter(u => u.createdAt && u.createdAt.getTime() >= from.getTime());
        }
        if (currentFilters.dateTo) {
          const to = parseDateSafe(currentFilters.dateTo);
          if (to) { to.setHours(23, 59, 59, 999); list = list.filter(u => u.createdAt && u.createdAt.getTime() <= to.getTime()); }
        }
        const total = list.length;
        const startIndex = (pageToFetch - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        setUsers(list.slice(startIndex, endIndex)); setTotalUsers(total); setHasMorePages(endIndex < total);
      }
    } catch (error: any) {
      if (addError) addError(`Error al cargar usuarios: ${error.message}`, undefined, `WorkspaceUsers-${pageToFetch}`);
      setUsers([]); setTotalUsers(0); setHasMorePages(false);
    } finally {
      setLoading(false); setLoadingPage(false); setPageChanging(false);
    }
  }, [currentUser, addError, viewTab, showDemos]);

  const handleTabChange = (tab: 'paid' | 'all') => {
    if (tab === viewTab) return;
    setViewTab(tab);
    setCurrentPageNum(1);
    // La vista de cobranza no usa los filtros avanzados → se limpian al entrar
    if (tab === 'paid') {
      setIsFilterOpen(false);
      // Conserva el filtro de vertical (siempre visible); limpia solo los avanzados de la pestaña "Todos".
      setFilters(prev => ({ plan: 'all', vertical: prev.vertical || 'all', dateFrom: null, dateTo: null, expirationStatus: 'all' }));
    }
  };

  useEffect(() => { setActiveFilters(filters); if (currentPageNum !== 1) setCurrentPageNum(1); }, [filters.plan, filters.vertical, filters.dateFrom, filters.dateTo, filters.expirationStatus, filters.onlyTrial]);

  useEffect(() => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    debounceTimeoutRef.current = setTimeout(() => {
      if (searchTerm !== activeSearchTerm) { setActiveSearchTerm(searchTerm); if (currentPageNum !== 1) setCurrentPageNum(1); searchTriggedRef.current = true; }
    }, SEARCH_DEBOUNCE_MS);
    return () => { if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current); };
  }, [searchTerm, activeSearchTerm, currentPageNum]);

  useEffect(() => {
    if (currentUser?.role === 'admin') { fetchUsers(currentPageNum, activeSearchTerm, activeFilters); searchTriggedRef.current = false; }
    else { setUsers([]); setTotalUsers(0); setCurrentPageNum(1); setHasMorePages(false); setLoading(false); setLoadingPage(false); setPageChanging(false); }
  }, [currentUser?.role, currentPageNum, activeSearchTerm, activeFilters, fetchUsers]);

  useEffect(() => { if (contentTopRef.current && !loading && !loadingPage) contentTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [currentPageNum, loading, loadingPage]);

  const handleFilterChange = (key: keyof AdminUserFilters, value: any) => { setFilters(prev => ({ ...prev, [key]: value })); };
  const clearAllFilters = () => { setSearchTerm(''); setFilters({ plan: 'all', vertical: 'all', dateFrom: null, dateTo: null, expirationStatus: 'all', onlyTrial: false }); setCurrentPageNum(1); };
  const countActiveFilters = () => { let c = 0; if (filters.plan !== 'all') c++; if (filters.vertical && filters.vertical !== 'all') c++; if (filters.dateFrom) c++; if (filters.dateTo) c++; if (filters.expirationStatus !== 'all') c++; if (filters.onlyTrial) c++; return c; };

  const handleNextPage = useCallback(() => { if (loading || loadingPage || pageChanging || !hasMorePages) return; setPageChanging(true); setCurrentPageNum(prev => prev + 1); }, [loading, loadingPage, pageChanging, hasMorePages]);
  const handlePrevPage = useCallback(() => { if (loading || loadingPage || pageChanging || currentPageNum <= 1) return; setPageChanging(true); setCurrentPageNum(prev => prev - 1); }, [loading, loadingPage, pageChanging, currentPageNum]);
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => { setSearchTerm(e.target.value); };

  const handleBranchPresetClick = (preset: number) => { setMaxBranches(preset); setShowCustomBranches(false); setCustomBranchesText(''); };
  const handleCustomBranchesClick = () => { setShowCustomBranches(true); setCustomBranchesText(String(maxBranches)); };
  const handleCustomBranchesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setCustomBranchesText(raw);
    const num = parseInt(raw, 10);
    if (!isNaN(num) && num >= 1) setMaxBranches(num);
  };
  const handleCustomBranchesBlur = () => {
    const num = parseInt(customBranchesText, 10);
    if (isNaN(num) || num < 1) { setMaxBranches(1); setCustomBranchesText('1'); }
    else { setMaxBranches(num); setCustomBranchesText(String(num)); }
  };
    
  const groupedUsers = useMemo<MonthlyUsers[]>(() => {
    if (!users || users.length === 0) return [];
    // Vista de cobranza: lista plana ya ordenada por vencimiento, sin agrupar por mes
    if (viewTab === 'paid' && (!activeFilters.expirationStatus || activeFilters.expirationStatus === 'all')) {
      return [{ monthKey: 'paid', displayMonth: 'Por vencimiento', users, count: users.length }];
    }
    const groups: Record<string, { users: UserSubscription[], representativeDate: Date }> = {};
    users.forEach(user => {
      const dateKey = user.createdAt ? formatLocalDateKey(user.createdAt) : 'unknown';
      if (!groups[dateKey]) groups[dateKey] = { users: [], representativeDate: user.createdAt || new Date() };
      groups[dateKey].users.push(user);
    });
    return Object.entries(groups).map(([monthKey, groupData]) => ({ monthKey, displayMonth: monthKey === 'unknown' ? 'Sin fecha' : formatMonthDisplay(groupData.representativeDate), users: groupData.users, count: groupData.users.length })).sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  }, [users, viewTab, activeFilters.expirationStatus]);

  // maxBranches ya viene en la fila del adaptador (no hace falta lectura extra).
  const startEditing = (user: UserSubscription) => {
    setEditingUser(user.uid);
    setSelectedPlan(user.subscription.plan);
    setBillingPeriod(periodFromCode(user.planCode));
    setPaymentMethod(user.subscription.paymentMethod || 'manual');
    setIsTrial((user.subscription as any).isTrial === true);

    setCustomPriceText(user.customPrice != null ? String(user.customPrice) : '');

    const userBranches = user.maxBranches ?? 1;
    setMaxBranches(userBranches);
    setShowCustomBranches(!BRANCH_PRESETS.includes(userBranches));
    setCustomBranchesText(String(userBranches));

    if (isPaidPlan(user.subscription.plan) && user.subscription.endDate) {
      const date = parseDateSafe(user.subscription.endDate);
      setEndDate(date ? toLocalYmd(date) : '');
    } else {
      setEndDate('');
    }
  };

  const cancelEditing = () => { setEditingUser(null); setShowCustomBranches(false); setCustomBranchesText(''); };

  // Al marcar "prueba/cortesía", el vencimiento se pone automáticamente a hoy + 30 días.
  const onToggleTrial = (checked: boolean) => {
    setIsTrial(checked);
    if (checked) {
      const d = new Date();
      d.setDate(d.getDate() + 30);
      setEndDate(toLocalYmd(d));
    }
  };

  const handleUpdateSubscription = async (userId: string, _businessId: string | null) => {
    setLoadingUpdate(userId); setSuccess(null); if(clearErrors) clearErrors();
    let finalEndDate: Date | null = null;
    if (isPaidPlan(selectedPlan)) {
      if (!endDate) { if(addError) addError('Fecha vencimiento requerida.', 'validation', `upd-${userId}`); setLoadingUpdate(null); return; }
      try {
        const [y, m, d] = endDate.split('-').map(Number);
        finalEndDate = new Date(); finalEndDate.setFullYear(y, m - 1, d); finalEndDate.setHours(23, 59, 59, 999);
        if (isNaN(finalEndDate.getTime())) throw new Error('Fecha inválida');
      } catch (e: any) { if(addError) addError(e.message || 'Fecha inválida.', 'validation', `upd-${userId}`); setLoadingUpdate(null); return; }
    }
    const finalMaxBranches = Math.max(1, Math.floor(maxBranches));
    const trialFlag = isPaidPlan(selectedPlan) ? isTrial : false;

    // Precio acordado: vacío = null (tarifa estándar). Solo aplica a planes de pago.
    let finalCustomPrice: number | null = null;
    if (isPaidPlan(selectedPlan) && customPriceText.trim() !== '') {
      const parsed = Number(customPriceText.replace(/[^0-9.]/g, ''));
      if (!Number.isFinite(parsed) || parsed < 0) {
        if (addError) addError('Precio acordado inválido.', 'validation', `upd-${userId}`);
        setLoadingUpdate(null); return;
      }
      finalCustomPrice = parsed;
    }

    try {
      // userId = uid = tenant_id. El server deriva isPro y persiste todo.
      await billingApi.updateSubscription(userId, {
        plan: selectedPlan,
        endDate: finalEndDate,
        status: 'active',
        paymentMethod: isPaidPlan(selectedPlan) ? (trialFlag ? 'cortesia' : paymentMethod) : null,
        isTrial: trialFlag,
        maxBranches: finalMaxBranches,
        customPrice: finalCustomPrice,
        billingPeriod,
      });
      setSuccess(`Suscripción actualizada${isPaidPlan(selectedPlan) ? ` (${periodAdjOf(mesesSel)})` : ''}. Sucursales: ${finalMaxBranches}.`
        + (finalCustomPrice != null ? ` Precio acordado: $${finalCustomPrice.toLocaleString('en-US')}${periodSuffixOf(mesesSel)}.` : ''));

      // ✅ FIX: Actualización optimista local (ES puede no tener maxBranches)
      setUsers(prev => prev.map(u =>
        u.uid === userId
          ? {
              ...u,
              maxBranches: finalMaxBranches,
              subscription: {
                ...u.subscription,
                plan: selectedPlan,
                endDate: finalEndDate,
                status: 'active' as const,
                paymentMethod: isPaidPlan(selectedPlan) ? (trialFlag ? 'cortesia' : paymentMethod) : null,
                isTrial: trialFlag,
              } as any
            }
          : u
      ));

      setEditingUser(null); setShowCustomBranches(false); setCustomBranchesText('');
      fetchUsers(currentPageNum, activeSearchTerm, activeFilters);
      setTimeout(() => setSuccess(null), 4000);
    } catch (error: any) {
      console.error('Error al actualizar suscripción:', error);
      if (addError) addError(`Error al actualizar: ${error.message}`, 'error', `upd-${userId}`);
    } finally { setLoadingUpdate(null); }
  };

  // ✅ Componente reutilizable: Selector de sucursales con presets
  const BranchSelector: React.FC = () => (
    <div>
      <label className="block text-xs font-medium text-content-2 mb-1.5">
        <Building className="h-3 w-3 inline mr-1" />Sucursales contratadas
      </label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {BRANCH_PRESETS.map((preset) => (
          <button key={preset} type="button" onClick={() => handleBranchPresetClick(preset)}
            className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${!showCustomBranches && maxBranches === preset ? 'border-accent bg-blue-50 dark:bg-accent/10 text-accent dark:text-accent ring-1 ring-accent' : 'border-line bg-surface text-content-2 hover:border-line hover:bg-blue-50 dark:hover:bg-accent/10'}`}
          >{preset}</button>
        ))}
        <button type="button" onClick={handleCustomBranchesClick}
          className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${showCustomBranches ? 'border-accent bg-blue-50 dark:bg-accent/10 text-accent dark:text-accent ring-1 ring-accent' : 'border-line bg-surface text-content-2 hover:border-line hover:bg-blue-50 dark:hover:bg-accent/10'}`}
        >Otro</button>
      </div>
      {showCustomBranches && (
        <input type="text" inputMode="numeric" pattern="[0-9]*" value={customBranchesText} onChange={handleCustomBranchesChange} onBlur={handleCustomBranchesBlur}
          placeholder="Cantidad" className="block w-24 py-1.5 px-2 text-sm border-line rounded-md focus:ring-accent focus:border-accent" autoFocus />
      )}
      {isPaidSel && maxBranches > 0 && (
        <p className="mt-1 text-xs text-accent">
          {maxBranches} sucursal{maxBranches > 1 ? 'es' : ''} = <span className="font-semibold">${totalBranchPrice.toLocaleString('es-MX')}{periodSuffix}</span>
          {maxBranches > 1 && (
            <span className="text-content-3"> · escalonado: {branchBreakdown(maxBranches).map(b => `${b.count}×$${b.price.toLocaleString('es-MX')}`).join(' + ')}</span>
          )}
        </p>
      )}

      {/* Periodicidad del cobro (13-ago-2026). Define cuánto dura el periodo que se activa al
          renovar (365 vs 30 días) y en qué unidad se lee el precio acordado de abajo. Mensual
          para los que pagan mes a mes (Kim, Karla); anual para el resto. */}
      {isPaidSel && (
        <div className="mt-3 pt-3 border-t border-line">
          <label className="block text-xs font-medium text-content-2 mb-1.5">
            <Calendar className="h-3 w-3 inline mr-1" />Periodicidad del cobro
          </label>
          <div className="flex gap-1.5">
            {([['yearly', 'Anual (365 días)'], ['semiannual', 'Semestral (182 días)'], ['monthly', 'Mensual (30 días)']] as Array<[BillingPeriod, string]>).map(([val, label]) => (
              <button key={val} type="button" onClick={() => setBillingPeriod(val)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${billingPeriod === val ? 'border-accent bg-blue-50 dark:bg-accent/10 text-accent dark:text-accent ring-1 ring-accent' : 'border-line bg-surface text-content-2 hover:border-line hover:bg-blue-50 dark:hover:bg-accent/10'}`}
              >{label}</button>
            ))}
          </div>
          {billingPeriod !== 'yearly' && (
            <p className="mt-1 text-xs text-content-3">
              Cada pago activa <span className="font-semibold">{isMonthlySel ? '30 días' : '182 días'}</span>.
              El precio acordado de abajo es <span className="font-semibold">{isMonthlySel ? 'por mes' : 'por semestre'}</span>.
            </p>
          )}
        </div>
      )}

      {/* Precio ACORDADO con el cliente (12-ago-2026). Vacío = cobra la tarifa estándar de
          arriba. Con valor, ESE es el monto que ve el dueño, el que se registra al pagar y
          el que cuenta en los ingresos del panel. NO subir sucursales para cuadrar un precio. */}
      {isPaidSel && (
        <div className="mt-3 pt-3 border-t border-line">
          <label className="block text-xs font-medium text-content-2 mb-1.5">
            Precio acordado <span className="text-content-3 font-normal">(opcional)</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-content-3">$</span>
            <input
              type="text" inputMode="decimal" value={customPriceText}
              onChange={(e) => setCustomPriceText(e.target.value)}
              placeholder={String(totalBranchPrice)}
              className="block w-32 py-1.5 px-2 text-sm border-line rounded-md focus:ring-accent focus:border-accent"
            />
            {customPriceText.trim() !== '' && (
              <button type="button" onClick={() => setCustomPriceText('')}
                className="text-xs text-content-3 underline hover:text-content-2">usar tarifa</button>
            )}
          </div>
          <p className="mt-1 text-xs text-content-3">
            {customPriceText.trim() === ''
              ? <>Vacío = tarifa estándar (<span className="font-semibold">${totalBranchPrice.toLocaleString('es-MX')}{periodSuffix}</span>).</>
              : <>Estándar ${totalBranchPrice.toLocaleString('es-MX')} · <span className="font-semibold text-accent">acordado ${(Number(customPriceText.replace(/[^0-9.]/g, '')) || 0).toLocaleString('es-MX')}{periodSuffix}</span></>}
          </p>
        </div>
      )}
    </div>
  );

  const activeFilterCount = countActiveFilters();
  const canGoPrev = currentPageNum > 1;
  const canGoNext = hasMorePages;

  return (
    <Layout>
      <div className="w-full space-y-6">
        <div ref={contentTopRef} id="content-top"></div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-line pb-4 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center"><Crown className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-yellow-500" />Administración de Suscripciones</h1>
            <p className="mt-1 text-sm text-content-2">Gestiona planes, renovaciones y estados</p>
          </div>
        </div>

        {success && (
          <div className="bg-green-100 dark:bg-green-500/20 border-l-4 border-green-500 text-green-700 dark:text-green-300 p-4 rounded-md shadow-sm animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
            <div className="flex"><div className="flex-shrink-0"><Check className="h-5 w-5 text-green-500" /></div><div className="ml-3"><p className="text-sm font-medium">{success}</p></div></div>
          </div>
        )}

        {/* Pestañas: cobranza (de paga) vs directorio completo */}
        <div className="flex gap-1 border-b border-line animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <button
            type="button"
            onClick={() => handleTabChange('paid')}
            className={`flex items-center px-4 pt-2 pb-2.5 -mb-px border-b-2 text-sm font-semibold transition-colors ${viewTab === 'paid' ? 'border-accent text-accent' : 'border-transparent text-content-2 hover:text-content'}`}
          >
            <Crown className="h-4 w-4 mr-1.5" />
            De paga{viewTab === 'paid' && !loading ? ` (${totalUsers})` : ''}
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('all')}
            className={`flex items-center px-4 pt-2 pb-2.5 -mb-px border-b-2 text-sm font-semibold transition-colors ${viewTab === 'all' ? 'border-accent text-accent' : 'border-transparent text-content-2 hover:text-content'}`}
          >
            <Users className="h-4 w-4 mr-1.5" />
            Todos los usuarios
          </button>
        </div>

        {/* Search & Filters */}
        <div className="bg-surface shadow sm:rounded-lg p-4 animate-fade-in-up space-y-4" style={{ animationDelay: '0.15s' }}>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 ${(loadingPage || pageChanging || loading) ? "text-blue-500 animate-pulse" : "text-content-3"}`} />
              </div>
              <input type="search" className="block w-full pl-10 pr-3 py-2 sm:text-sm border-line rounded-md leading-5 bg-surface placeholder-content-3 focus:outline-none focus:placeholder-content-3 focus:ring-1 focus:ring-accent focus:border-accent" placeholder="Buscar por Email, Nombre o Negocio..." value={searchTerm} onChange={handleSearchChange} disabled={loading || loadingPage || pageChanging} />
            </div>
            {/* Filtro de Vertical: SIEMPRE visible (ambas pestañas: cobranza y todos) */}
            <select
              value={filters.vertical || 'all'}
              onChange={(e) => handleFilterChange('vertical', e.target.value)}
              title="Filtrar por vertical"
              className="w-full sm:w-auto pl-3 pr-8 py-2 text-sm border-line bg-surface rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            >
              {VERTICAL_FILTER_OPTIONS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-content-2 whitespace-nowrap cursor-pointer" title="Negocios de demostración/QA de la casa y negocios archivados: no son clientes activos ni suman en métricas">
              <input type="checkbox" checked={showDemos} onChange={(e) => { setShowDemos(e.target.checked); setCurrentPageNum(1); }} className="rounded border-line text-accent focus:ring-accent" />
              Mostrar demos y archivados
            </label>
            {viewTab === 'all' && (
            <button onClick={() => setIsFilterOpen(!isFilterOpen)} className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md w-full sm:w-auto justify-center transition-colors ${isFilterOpen || activeFilterCount > 0 ? 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-300' : 'border-line text-content-2 bg-surface hover:bg-surface-2'}`}>
              <Filter className="h-4 w-4 mr-2" />Filtros
              {activeFilterCount > 0 && <span className="ml-2 bg-accent-solid text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{activeFilterCount}</span>}
              {isFilterOpen ? <ChevronUp className="ml-2 h-4 w-4" /> : <ChevronDown className="ml-2 h-4 w-4" />}
            </button>
            )}
          </div>

          {isFilterOpen && (
            <div className="pt-4 border-t border-line grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
              <div>
                <label className="block text-xs font-medium text-content-2 mb-1">Plan</label>
                <select value={filters.plan} onChange={(e) => handleFilterChange('plan', e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-accent sm:text-sm rounded-md">
                  <option value="all">Todos los planes</option><option value="pro">PRO</option><option value="premium">Premium (legacy)</option><option value="free">Gratis</option><option value="onboarding">Onboarding</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-2 mb-1">Estado de Vencimiento</label>
                <select value={filters.expirationStatus} onChange={(e) => handleFilterChange('expirationStatus', e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-accent focus:border-accent sm:text-sm rounded-md">
                  <option value="all">Todos los estados</option><option value="expired">Vencidos</option><option value="expiring-soon">Por vencer (≤7 días)</option><option value="active">Activos (No vencidos)</option>
                </select>
              </div>
              <div className="flex gap-2 lg:col-span-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-content-2 mb-1">Registro Desde</label>
                  <input type="date" className="block w-full py-2 px-3 border border-line bg-surface rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent sm:text-sm" value={filters.dateFrom ? (filters.dateFrom instanceof Date ? filters.dateFrom.toISOString().split('T')[0] : filters.dateFrom) as string : ''} onChange={(e) => handleFilterChange('dateFrom', e.target.value ? new Date(e.target.value) : null)} />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-content-2 mb-1">Hasta</label>
                  <input type="date" className="block w-full py-2 px-3 border border-line bg-surface rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent sm:text-sm" value={filters.dateTo ? (filters.dateTo instanceof Date ? filters.dateTo.toISOString().split('T')[0] : filters.dateTo) as string : ''} onChange={(e) => handleFilterChange('dateTo', e.target.value ? new Date(e.target.value) : null)} />
                </div>
              </div>
              <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
                <button onClick={clearAllFilters} className="text-sm text-content-3 hover:text-content-2 flex items-center"><X className="h-4 w-4 mr-1" />Limpiar filtros</button>
              </div>
            </div>
          )}

          {activeFilterCount > 0 && !isFilterOpen && (
            <div className="flex flex-wrap gap-2 pt-2">
              {filters.plan !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300">Plan: {filters.plan}</span>}
              {filters.vertical && filters.vertical !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300">Vertical: {VERTICAL_META[filters.vertical]?.label || filters.vertical}</span>}
              {filters.expirationStatus !== 'all' && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300">Estado: {filters.expirationStatus === 'expired' ? 'Vencido' : filters.expirationStatus === 'expiring-soon' ? 'Por vencer' : 'Activo'}</span>}
              {filters.onlyTrial && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300">Solo pruebas</span>}
              {(filters.dateFrom || filters.dateTo) && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-canvas text-content">Fechas activas</span>}
              <button onClick={clearAllFilters} className="text-xs text-content-3 underline ml-2">Limpiar</button>
            </div>
          )}
        </div>
        
        {/* Desktop View */}
        <div className="hidden md:block">
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="px-4 py-4 sm:px-6 bg-surface-2 border-b border-line">
              <h3 className="text-lg leading-6 font-medium text-content flex items-center"><User className="h-5 w-5 mr-2 text-content-3" /> Usuarios</h3>
            </div>

            {loading && users.length === 0 && (<div className="py-12 text-center"><BrandLoader size={36} /><p className="mt-3 text-sm font-medium text-content-3">Cargando usuarios...</p></div>)}
            {!loadingPage && !pageChanging && users.length === 0 && !loading && (<div className="text-center py-16"><Search className="h-12 w-12 text-content-3 mx-auto mb-3" /><p className="text-lg font-medium text-content-2">No se encontraron usuarios</p><p className="text-sm text-content-3 mt-1">Prueba ajustando los filtros.</p></div>)}

            {(!loading || users.length > 0) && groupedUsers.length > 0 && (
              <div className="space-y-8 p-6">
                {groupedUsers.map((group, groupIndex) => (
                  <div key={group.monthKey} className="animate-fade-in-up" style={{ animationDelay: `${0.3 + groupIndex * 0.1}s` }}>
                    <div className="flex items-center mb-3">
                      <h3 className="text-lg font-medium text-content flex items-center"><Calendar className="h-5 w-5 mr-2 text-content-3" />{group.displayMonth}</h3>
                      <div className="ml-3 px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center"><Users className="h-3 w-3 mr-1" />{group.count}</div>
                    </div>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full divide-y divide-line">
                        <thead className="bg-surface-2">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Usuario</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Negocio</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Plan</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Estado</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">Vencimiento</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider" title="Último rastro, actividad y riesgo de renovación; pasa el ratón o toca para el detalle">Uso</th>
                            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-content-3 uppercase tracking-wider">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="bg-surface divide-y divide-line">
                          {group.users.map((user) => {
                            const expired = isExpired(user.subscription.endDate);
                            const expiring = isExpiringSoon(user.subscription.endDate);
                            const daysLeft = getDaysRemaining(user.subscription.endDate);
                            return (
                              <React.Fragment key={user.uid}>
                                <tr className={`${expired ? 'bg-red-50 dark:bg-red-500/10' : ''} ${expiring ? 'bg-orange-50 dark:bg-orange-500/10' : ''} ${editingUser === user.uid ? 'opacity-50' : ''} hover:bg-canvas transition-colors duration-150 align-middle`}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {user.phoneNumber ? (<ClientContactInfo clientName={user.displayName} phoneNumber={user.phoneNumber} countryCode={user.countryCode || null} email={user.email} showName={true} nameAsLink={false} avatarUrl={(user as any).avatarUrl || null} />) : (
                                      <div className="flex items-center"><div className="flex-shrink-0 h-10 w-10 bg-canvas rounded-full flex items-center justify-center overflow-hidden">{(user as any).avatarUrl ? <img src={(user as any).avatarUrl} alt="" className="h-10 w-10 object-cover" loading="lazy" data-testid="contacto-avatar" /> : <User className="h-6 w-6 text-content-3" />}</div><div className="ml-4"><div className="text-sm font-medium text-content">{user.displayName}</div><p className="text-sm text-content-3 mt-1 truncate">{user.email}</p></div></div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center text-sm text-content">{user.businessId && <Building className="h-4 w-4 text-content-3 mr-1.5 flex-shrink-0" />}<span className="truncate max-w-[150px]" title={user.businessName || ''}>{truncateName(user.businessName)}</span></div>
                                    <div className="mt-1"><VerticalBadge vertical={user.vertical} /></div>
                                    {(user.maxBranches ?? 1) > 1 && (<div className="mt-0.5 text-xs text-accent flex items-center"><Building className="h-3 w-3 mr-1" />{user.maxBranches} sucursales</div>)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPlanBadgeClasses(user.subscription.plan)}`}>{getPlanLabel(user.subscription.plan)}</span>
                                    {(user as any).isDemo && (<span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300">Demo</span>)}
                                    {(user as any).isArchived && (<span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300">Archivado</span>)}
                                    {(user.subscription as any).isTrial && (<span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">Prueba</span>)}
                                    {user.subscription.paymentMethod && isPaidPlan(user.subscription.plan) && !(user.subscription as any).isTrial && (<div className="mt-1 flex items-center text-xs text-content-3"><CreditCard className="h-3.5 w-3.5 mr-1" />{PAYMENT_METHODS.find(m => m.id === user.subscription.paymentMethod)?.name || user.subscription.paymentMethod}</div>)}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${expired ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300' : user.subscription.status === 'active' ? 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300' : 'bg-canvas text-content'}`}>{expired ? 'Vencido' : user.subscription.status === 'active' ? 'Activo' : 'Cancelado'}</span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-content-3">
                                    {(isPaidPlan(user.subscription.plan) && user.subscription?.endDate) ? (
                                      <div className="space-y-1">
                                        <div className="flex items-center"><CalendarIcon className="h-4 w-4 mr-1.5 text-content-3 flex-shrink-0" /><span>{new Intl.DateTimeFormat('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(user.subscription.endDate)}</span></div>
                                        {daysLeft !== null && (<span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${daysLeft < 0 ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300' : daysLeft === 0 ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300' : daysLeft <= 7 ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-300' : 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300'}`}>{daysLeft < 0 ? 'Vencido' : daysLeft === 0 ? 'Vence hoy' : `${daysLeft} día${daysLeft !== 1 ? 's' : ''}`}</span>)}
                                      </div>
                                    ) : (isPaidPlan(user.subscription.plan) ? <span className='text-red-600 dark:text-red-400'>¡Falta!</span> : 'N/A')}
                                  </td>
                                  <td className="px-6 py-4">
                                    <UsoCelda tenantId={user.uid} uso={uso[String(user.uid)]} cargando={usoCargando} nombre={user.businessName} />
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                    <button type="button" onClick={() => startEditing(user)} disabled={!!editingUser || loadingUpdate === user.uid} className="text-accent hover:opacity-80 disabled:text-content-3 disabled:cursor-not-allowed">Modificar</button>
                                  </td>
                                </tr>

                                {editingUser === user.uid && (
                                  <tr className="bg-blue-50 dark:bg-accent/10 border-l-4 border-accent animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                                    <td colSpan={7} className="px-4 py-4 sm:px-6">
                                      <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
                                          <div>
                                            <label htmlFor={`plan-${user.uid}`} className="block text-xs font-medium text-content-2">Plan</label>
                                            <select id={`plan-${user.uid}`} value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value as PlanType)} className="mt-1 block w-full pl-3 pr-10 py-1.5 text-sm border-line rounded-md">
                                              <option value="free">Gratis</option><option value="pro">PRO</option><option value="premium">Premium (legacy)</option><option value="onboarding">Onboarding</option>
                                            </select>
                                          </div>
                                          <div className={`${isPaidPlan(selectedPlan) ? '' : 'opacity-50'}`}>
                                            <label htmlFor={`endDate-${user.uid}`} className="block text-xs font-medium text-content-2">Vencimiento</label>
                                            <input id={`endDate-${user.uid}`} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required={isPaidPlan(selectedPlan)} className="mt-1 block w-full py-1.5 text-sm border-line rounded-md disabled:bg-canvas" disabled={!isPaidPlan(selectedPlan)} />
                                          </div>
                                          <div className={`${isPaidPlan(selectedPlan) ? '' : 'opacity-50'}`}>
                                            <label htmlFor={`payment-${user.uid}`} className="block text-xs font-medium text-content-2">Método Pago</label>
                                            <select id={`payment-${user.uid}`} value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-1.5 text-sm border-line rounded-md disabled:bg-canvas" disabled={!isPaidPlan(selectedPlan)}>
                                              {PAYMENT_METHODS.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}
                                            </select>
                                          </div>
                                          <div className="flex justify-end space-x-3">
                                            <button type="button" onClick={cancelEditing} disabled={loadingUpdate === user.uid} className="inline-flex justify-center py-1.5 px-3 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50">Cancelar</button>
                                            <button type="button" onClick={() => handleUpdateSubscription(user.uid, user.businessId)} disabled={loadingUpdate === user.uid} className="inline-flex justify-center items-center py-1.5 px-3 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-accent-solid hover:bg-accent-solid/90 disabled:opacity-50">{loadingUpdate === user.uid ? (<Loader2 className="h-4 w-4 animate-spin" />) : 'Guardar'}</button>
                                          </div>
                                        </div>
                                        {isPaidPlan(selectedPlan) && (
                                          <label className="flex items-center gap-2 text-sm text-content-2 cursor-pointer">
                                            <input type="checkbox" checked={isTrial} onChange={(e) => onToggleTrial(e.target.checked)} className="rounded border-line text-amber-600 focus:ring-amber-500" />
                                            <span>Marcar como <b>prueba / cortesía</b> (acceso {selectedPlan === 'premium' ? 'Premium' : 'Pro'} completo, pero <b>no cuenta como ingreso</b>)</span>
                                          </label>
                                        )}
                                        <BranchSelector />
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Mobile View */}
        <div className="md:hidden">
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="px-4 py-4 bg-surface-2 border-b border-line">
              <div className="flex justify-between items-center">
                <h3 className="text-base font-medium text-content flex items-center"><User className="h-5 w-5 mr-2 text-content-3" /> Usuarios</h3>
                {(!loading || users.length > 0) && (<div className="flex items-center"><Users className="h-4 w-4 mr-1 text-blue-500" /><span className="text-sm text-content-3">{totalUsers}</span></div>)}
              </div>
            </div>
            
            {loading && users.length === 0 && (<div className="py-8 text-center"><BrandLoader size={36} /><p className="mt-2 text-sm font-medium text-content-3">Cargando usuarios...</p></div>)}
            {!loadingPage && !pageChanging && users.length === 0 && !loading && (<div className="p-6 text-center"><Search className="h-12 w-12 text-content-3 mx-auto mb-3" /><p className="text-base font-medium text-content-2">No se encontraron usuarios</p></div>)}
            
            {users.length > 0 && (
              <div className="space-y-6 py-4">
                {groupedUsers.map((group, groupIndex) => (
                  <div key={group.monthKey} className="animate-fade-in-up" style={{ animationDelay: `${0.3 + groupIndex * 0.1}s` }}>
                    <div className="px-4 py-2 bg-canvas flex items-center">
                      <h3 className="text-base font-medium text-content">{group.displayMonth}</h3>
                      <div className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center"><Users className="h-3 w-3 mr-1" />{group.count}</div>
                    </div>
                    <div className="divide-y divide-line">
                      {group.users.map((user) => {
                        const expired = isExpired(user.subscription.endDate);
                        const expiring = isExpiringSoon(user.subscription.endDate);
                        const daysLeft = getDaysRemaining(user.subscription.endDate);
                        return (
                          <div key={user.uid} className={`px-4 py-4 ${expired ? 'bg-red-50 dark:bg-red-500/10' : expiring ? 'bg-orange-50 dark:bg-orange-500/10' : editingUser === user.uid ? 'opacity-50 bg-surface-2' : 'hover:bg-surface-2'} transition-all duration-150`}>
                            <div className="flex justify-between items-start">
                              <div>
                                {user.phoneNumber ? (<ClientContactInfo clientName={user.displayName} phoneNumber={user.phoneNumber} countryCode={user.countryCode || null} email={user.email} showName={true} nameAsLink={false} avatarUrl={(user as any).avatarUrl || null} />) : (<div className="flex items-center">{(user as any).avatarUrl && <img src={(user as any).avatarUrl} alt="" className="flex-shrink-0 h-9 w-9 rounded-full object-cover mr-3" loading="lazy" data-testid="contacto-avatar" />}<div><div className="text-sm font-medium text-content">{user.displayName || 'Usuario'}</div><div className="text-xs text-content-3 mt-0.5 truncate max-w-[180px]">{user.email}</div></div></div>)}
                                <div className="mt-2 flex flex-wrap gap-2">
                                  <VerticalBadge vertical={user.vertical} />
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPlanBadgeClasses(user.subscription.plan)}`}>{getPlanLabel(user.subscription.plan)}</span>
                                  {(user as any).isDemo && (<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300">Demo</span>)}
                                  {(user as any).isArchived && (<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-200 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300">Archivado</span>)}
                                  {(user.subscription as any).isTrial && (<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">Prueba</span>)}
                                  {isPaidPlan(user.subscription.plan) && user.subscription.endDate && daysLeft !== null && (<span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${daysLeft < 0 ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300' : daysLeft === 0 ? 'bg-red-100 dark:bg-red-500/20 text-red-800 dark:text-red-300' : daysLeft <= 7 ? 'bg-orange-100 dark:bg-orange-500/20 text-orange-800 dark:text-orange-300' : 'bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300'}`}>{daysLeft < 0 ? 'Vencido' : daysLeft === 0 ? 'Vence hoy' : `${daysLeft} día${daysLeft !== 1 ? 's' : ''}`}</span>)}
                                  {(user.maxBranches ?? 1) > 1 && (<span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300"><Building className="h-3 w-3 mr-1" />{user.maxBranches} suc.</span>)}
                                </div>
                                {/* Cómo lo usan (toca para el detalle) */}
                                <div className="mt-2">
                                  <UsoCelda tenantId={user.uid} uso={uso[String(user.uid)]} cargando={usoCargando} nombre={user.businessName} />
                                </div>
                              </div>
                              <div className="flex flex-col items-end space-y-2">
                                <button onClick={() => startEditing(user)} disabled={!!editingUser} className="text-xs text-accent hover:opacity-80">Modificar</button>
                              </div>
                            </div>
                            
                            {editingUser === user.uid && (
                              <div className="mt-3 pt-3 border-t border-line bg-blue-50 dark:bg-accent/10 -mx-4 px-4 py-3">
                                <div className="space-y-3">
                                  <div>
                                    <label className="block text-xs font-medium text-content-2 mb-1">Plan</label>
                                    <select value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value as PlanType)} className="block w-full pl-3 pr-10 py-2 text-sm border-line rounded-md">
                                      <option value="free">Gratis</option><option value="pro">PRO</option><option value="premium">Premium (legacy)</option><option value="onboarding">Onboarding</option>
                                    </select>
                                  </div>
                                  {isPaidPlan(selectedPlan) && (<div><label className="block text-xs font-medium text-content-2 mb-1">Vencimiento</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="block w-full py-2 text-sm border-line rounded-md" /></div>)}
                                  {isPaidPlan(selectedPlan) && (<div><label className="block text-xs font-medium text-content-2 mb-1">Método de Pago</label><select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border-line rounded-md">{PAYMENT_METHODS.map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}</select></div>)}
                                  {isPaidPlan(selectedPlan) && (<label className="flex items-center gap-2 text-sm text-content-2 cursor-pointer"><input type="checkbox" checked={isTrial} onChange={(e) => onToggleTrial(e.target.checked)} className="rounded border-line text-amber-600 focus:ring-amber-500" /><span>Prueba / cortesía (no cuenta como ingreso)</span></label>)}
                                  <BranchSelector />
                                  <div className="flex justify-end space-x-3 mt-2">
                                    <button type="button" onClick={cancelEditing} disabled={loadingUpdate === user.uid} className="inline-flex justify-center py-2 px-3 border border-line text-sm font-medium rounded-md text-content-2 bg-surface disabled:opacity-50">Cancelar</button>
                                    <button type="button" onClick={() => handleUpdateSubscription(user.uid, user.businessId)} disabled={loadingUpdate === user.uid} className="inline-flex justify-center py-2 px-3 border border-transparent text-sm font-medium rounded-md text-white bg-accent-solid disabled:opacity-50">Guardar</button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {(!loading || users.length > 0) && totalUsers > 0 && (
          <div className="mt-6">
            <PaginationControls currentPage={currentPageNum} isLoading={loadingPage || pageChanging} onPrevPage={handlePrevPage} onNextPage={handleNextPage} canGoPrev={canGoPrev && !pageChanging} canGoNext={canGoNext && !pageChanging} itemsPerPage={PAGE_SIZE} countOnPage={users.length} totalItems={totalUsers} />
          </div>
        )}

        {pageChanging && (<div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50"><Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" /><span className="text-sm font-medium text-content-2">Cambiando página...</span></div>)}
      </div>
    </Layout>
  );
};

export default AdminSubscriptionsPage;